/**
 * `T-33` · El contrato de la **subida de imágenes con URL firmada** — `ADR-003`
 * D-2, corregido por lo que `V-12` midió el 2026-09-10.
 *
 * Se añade aquí y no se toca nada de lo que ya había: `menu.ts` está congelado
 * durante la ola 2, y este archivo describe una superficie nueva.
 *
 * ── Lo que este contrato tiene que hacer cumplir, y por qué ─────────────────
 *
 * `V-12` midió el token de subida de Supabase contra `fialrest-test` y encontró
 * tres cosas que `ADR-003` daba por hechas y **no son verdad**:
 *
 *  1. **El token NO es de un solo uso.** Borrado el objeto, el mismo token
 *     volvió a escribir. El uso único lo impone T-33: ruta con **nonce**.
 *  2. **Vive 7 200 s y `expiresIn` se ignora** — se pidieron 5 s y salió con
 *     dos horas. Cada permiso es una *capacidad de escritura reutilizable
 *     durante dos horas* sobre esa ruta. El nonce es lo que hace que una fuga
 *     escriba donde nadie mira.
 *  3. **El token no acota el `Content-Type`**: subió `text/plain` con 200. El
 *     tope lo pone el **cubo** con `allowed_mime_types`, no el token.
 *
 * De ahí que el navegador tenga que declarar `tipo` y `bytes` **antes** de que
 * se firme nada: el rechazo llega antes de emitir el token, no después de subir
 * doce megas.
 */
import { z } from 'zod';
/**
 * Los tres tipos que se aceptan, y la lista es **cerrada a propósito**.
 *
 * > [!danger] `image/svg+xml` NO está, y no es un olvido
 * > Un SVG es un documento con script dentro. Servido desde el dominio de
 * > Storage —que es de donde el comensal descarga la foto del platillo— un SVG
 * > subido por cualquiera con un permiso de subida sería código ejecutándose en
 * > ese origen. Por la misma razón el cubo se configura con **la lista, nunca
 * > con `image/*`**: el comodín incluye SVG.
 *
 * `image/webp` entra porque es el formato al que `T-35` va a redimensionar: sin
 * él, el redimensionado del navegador no tendría a dónde escribir.
 */
export const TIPOS_DE_IMAGEN_ACEPTADOS = ['image/jpeg', 'image/png', 'image/webp'];
/** La extensión con la que se nombra el objeto, por tipo. Una sola definición. */
export const EXTENSION_POR_TIPO = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
};
/**
 * 12 MiB. **Medido, no elegido**: `V-15` subió 4, 8 y 12 MiB sin procesar contra
 * `fialrest-test` el 2026-09-10, y el control negativo —2 MiB contra un cubo con
 * tope de 1 MiB— rebotó. O sea que el tope se hace cumplir y que doce caben.
 *
 * No es el tope que le importa al comensal: `V-14` midió que **no hay
 * transformaciones de imagen en este proyecto**, así que la que sube el dueño es
 * la que descarga el comensal. Eso lo arregla `T-35` redimensionando en el
 * navegador, por peso de DESCARGA. Esto es sólo el techo de lo que la puerta
 * admite.
 */
export const TOPE_DE_SUBIDA_BYTES = 12 * 1024 * 1024;
/**
 * Lo que vive de verdad un token de subida: **7 200 s**, medido en `V-12` el
 * 2026-09-10. No se pide, no se puede acortar y no se supone — se pidieron 5 s
 * y salió con esto.
 *
 * Viaja en el contrato porque el navegador tiene que saber cuándo dejar de
 * intentar, y porque un número que se afirma sin haberlo medido es una mentira
 * con retraso.
 */
export const VIDA_DEL_TOKEN_SEGUNDOS = 7200;
export const esquemaTipoDeImagen = z.enum(TIPOS_DE_IMAGEN_ACEPTADOS);
/**
 * Dónde va la imagen. Tres destinos y ni uno más: son los tres que `T-35`
 * necesita —portada, logotipo y foto por platillo—, y la lista cerrada es lo que
 * impide que mañana alguien invente un cuarto que nadie acotó.
 *
 * Los valores van en inglés como el resto de los conjuntos cerrados del
 * proyecto (`draft`/`published`, `active`/`suspended`): el motor los guarda con
 * un CHECK y el CHECK y esta lista tienen que decir lo mismo.
 */
export const esquemaDestinoDeImagen = z.enum(['item', 'cover', 'logo']);
const esquemaIdDeSubida = z.string().uuid();
/**
 * Lo que el navegador manda para **pedir permiso**.
 *
 * `tipo` y `bytes` son obligatorios y no son cortesía: son lo único con lo que
 * el servidor puede rechazar **antes** de firmar. Un cliente puede mentir en
 * `bytes` —y entonces lo para el `file_size_limit` del cubo—, pero no puede
 * omitirlos, y por eso el rechazo del caso honrado cuesta una petición de 200
 * bytes en vez de una subida entera.
 *
 * `.strict()`: un campo de más es un cliente que cree estar hablando con otra
 * versión de esta API, y contestarle 200 es dejarlo seguir equivocado.
 *
 * > [!info] El esquema comprueba la FORMA; la política la decide el servicio
 * > `tipo` entra como cadena y no como `esquemaTipoDeImagen`, y `bytes` no lleva
 * > tope aquí. No es dejadez: es que «ese tipo no vale» y «eso pesa de más» son
 * > **415** y **413**, dos respuestas que le dicen a quien está delante qué
 * > hacer, y meterlas en el esquema las convertiría a las dos en un 400 genérico
 * > junto a «te falta un campo».
 * >
 * > El editor valida contra `TIPOS_DE_IMAGEN_ACEPTADOS` y `TOPE_DE_SUBIDA_BYTES`
 * > —que salen de aquí, así que sigue habiendo **una sola definición**— antes de
 * > pedir nada; el servidor no se fía y lo comprueba otra vez.
 */
export const esquemaPedirPermisoDeSubida = z.object({
    destino: esquemaDestinoDeImagen,
    /** Sólo para `item`, y obligatorio ahí: es sobre lo que se autoriza. */
    platilloId: esquemaIdDeSubida.optional(),
    tipo: z.string().trim().min(1).max(100),
    bytes: z.number().int().positive(),
}).strict().refine((p) => (p.destino === 'item') === (p.platilloId !== undefined), {
    message: 'La foto de un platillo necesita su `platilloId`, y la portada y el ' +
        'logotipo no lo admiten: no cuelgan de ningún platillo.',
    path: ['platilloId'],
});
/**
 * Lo que la API devuelve. **La credencial privilegiada no está aquí**, y no
 * puede estarlo: lo que viaja es un permiso sobre **una ruta**, que es
 * exactamente lo que `ADR-003` decidió exponer.
 *
 * `venceEn` son los 7 200 s medidos. `permisoId` es lo que el navegador
 * devuelve al confirmar, y es la pieza con la que **nosotros** imponemos el uso
 * único que Storage no impone.
 */
export const esquemaPermisoDeSubida = z.object({
    permisoId: esquemaIdDeSubida,
    /** Empieza SIEMPRE por el `tenant_id`, y lleva un nonce. Ver `ADR-003`. */
    ruta: z.string().min(1),
    urlDeSubida: z.string().url(),
    token: z.string().min(1),
    tipo: esquemaTipoDeImagen,
    venceEn: z.number().int().positive(),
}).strict();
/**
 * Confirmar que la subida terminó. **No lleva cuerpo**: todo lo que hay que
 * saber está en el permiso, y aceptar la ruta desde el navegador sería dejar
 * que el cliente diga dónde escribió — que es justo lo que la ruta firmada
 * existe para no tener que creerle.
 */
export const esquemaSubidaConfirmada = z.object({
    permisoId: esquemaIdDeSubida,
    ruta: z.string().min(1),
}).strict();
/** ¿Es uno de los tres tipos aceptados? Lo usan la API y el editor. */
export const esTipoAceptado = (tipo) => TIPOS_DE_IMAGEN_ACEPTADOS.includes(tipo);
//# sourceMappingURL=subida.js.map